import { useState, useEffect, useCallback } from 'react';
import { BarChart2, Mail, AlertCircle, CheckCircle, Clock, Users, TrendingUp, Inbox, Star, Archive, User, Settings } from 'lucide-react';
import { getDashboardStats } from '@/api';
import { useAuthStore, useThreadStore } from '@/store';
import { DashboardStats } from '@/types';
import { useToast } from '@/components/ui/Toaster';
import clsx from 'clsx';

const statCards = [
  { key: 'total', label: 'Total Threads', icon: Inbox, color: 'bg-blue-500' },
  { key: 'new', label: 'New', icon: Mail, color: 'bg-signal-500' },
  { key: 'assigned', label: 'Assigned', icon: User, color: 'bg-blue-500' },
  { key: 'in_progress', label: 'In Progress', icon: Clock, color: 'bg-yellow-500' },
  { key: 'replied', label: 'Replied', icon: CheckCircle, color: 'bg-purple-500' },
  { key: 'closed', label: 'Closed', icon: Archive, color: 'bg-gray-500' },
  { key: 'spam', label: 'Spam', icon: AlertCircle, color: 'bg-red-500' },
] as const;

export default function Dashboard() {
  const { user } = useAuthStore();
  const { setLoading, setError } = useThreadStore();
  const { addToast } = useToast();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLocalLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setLocalLoading(true);
      setLoading(true);
      try {
        const result = await getDashboardStats();
        if (result.ok && result.data) {
          setStats(result.data);
        } else {
          setError(result.error || 'Failed to load dashboard');
          addToast(result.error || 'Failed to load dashboard', 'error');
        }
      } catch (e) {
        setError('Network error');
        addToast('Network error', 'error');
      } finally {
        setLocalLoading(false);
        setLoading(false);
      }
    };
    fetchStats();
  }, [setLoading, setError, addToast]);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Overview of your Signal Inbox</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => {
          const value = card.key === 'total' ? stats?.total : stats?.byStatus[card.key] || 0;
          const Icon = card.icon;
          return (
            <div key={card.key} className="card p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{card.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
                </div>
                <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center', card.color)}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* By Domain */}
        <div className="card p-5 lg:col-span-1">
          <h3 className="font-medium text-gray-900 mb-4">By Domain</h3>
          <div className="space-y-3">
            {stats?.byDomain.map((d) => (
              <div key={d.domain} className="flex items-center gap-3">
                <span className="w-24 text-sm text-gray-500 truncate">{d.domain}</span>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-signal-500 rounded-full" style={{ width: `${stats.total > 0 ? (d.count / stats.total) * 100 : 0}%` }} />
                </div>
                <span className="w-12 text-sm text-gray-700 text-right font-medium">{d.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Category */}
        <div className="card p-5 lg:col-span-1">
          <h3 className="font-medium text-gray-900 mb-4">By Category</h3>
          <div className="space-y-3">
            {stats?.byCategory.map((c) => (
              <div key={c.category} className="flex items-center gap-3">
                <span className="w-24 text-sm text-gray-500 truncate capitalize">{c.category}</span>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500 rounded-full" style={{ width: `${stats.total > 0 ? (c.count / stats.total) * 100 : 0}%` }} />
                </div>
                <span className="w-12 text-sm text-gray-700 text-right font-medium">{c.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Priority */}
        <div className="card p-5 lg:col-span-1">
          <h3 className="font-medium text-gray-900 mb-4">By Priority</h3>
          <div className="space-y-3">
            {stats?.byPriority.map((p) => {
              const colors: Record<string, string> = { high: 'bg-red-500', medium: 'bg-yellow-500', low: 'bg-gray-500' };
              return (
                <div key={p.priority} className="flex items-center gap-3">
                  <span className="w-24 text-sm text-gray-500 truncate capitalize">{p.priority}</span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className={clsx('h-full rounded-full', colors[p.priority])} style={{ width: `${stats.total > 0 ? (p.count / stats.total) * 100 : 0}%` }} />
                  </div>
                  <span className="w-12 text-sm text-gray-700 text-right font-medium">{p.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-5">
        <h3 className="font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button className="btn-secondary">
            <Mail className="w-4 h-4" /> New Email
          </button>
          <button className="btn-secondary">
            <Users className="w-4 h-4" /> Manage Team
          </button>
          <button className="btn-secondary">
            <TrendingUp className="w-4 h-4" /> View Reports
          </button>
          <button className="btn-secondary">
            <Settings className="w-4 h-4" /> Settings
          </button>
        </div>
      </div>
    </div>
  );
}